import type { Activity, Participant } from './types'
import { ACTIVITY_COLORS, ACTIVITY_STROKE_COLORS, SELECTION_COLOR } from './constants'
import { getActivityWidth } from './geometry'

interface Props {
  activity: Activity
  isSelected: boolean
  isTransitionStart: boolean
  participants: Participant[]
  theme: 'dark' | 'light'
}

export default function ActivityNode({
  activity,
  isSelected,
  isTransitionStart,
  participants,
  theme,
}: Props) {
  const fill = ACTIVITY_COLORS[activity.type] || ACTIVITY_COLORS.activity
  const defaultStroke = ACTIVITY_STROKE_COLORS[activity.type] || ACTIVITY_STROKE_COLORS.activity
  const strokeColor = isSelected || isTransitionStart ? SELECTION_COLOR : defaultStroke
  const strokeWidth = isTransitionStart ? 4 : isSelected ? 3 : 1.5

  const icon = activity.icon || ''
  const computedWidth = getActivityWidth(activity)

  // Performer badge
  const performer = activity.performer
    ? participants.find(p => p.id === activity.performer)
    : null

  // Text X position shifts right when icon present
  const textX = icon
    ? activity.x + 10 + computedWidth / 2
    : activity.x + computedWidth / 2

  return (
    <g>
      {/* Main rect */}
      <rect
        x={activity.x}
        y={activity.y}
        width={computedWidth}
        height={activity.height}
        fill={fill}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        rx={4}
        ry={4}
      />

      {/* Icon (rendered left of text) */}
      {icon && (
        <text
          x={activity.x + 12}
          y={activity.y + activity.height / 2}
          textAnchor="start"
          dominantBaseline="central"
          fill="rgba(255,255,255,0.9)"
          fontSize={16}
          fontFamily="Arial, sans-serif"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {icon}
        </text>
      )}

      {/* Activity label */}
      <text
        x={textX}
        y={activity.y + activity.height / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill="#ffffff"
        fontSize={13}
        fontFamily="Arial, sans-serif"
        fontWeight={500}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {activity.description}
      </text>

      {/* Performer badge below the activity */}
      {performer && (
        <g>
          <rect
            x={activity.x + (computedWidth - getPerformerWidth(performer.name)) / 2 - 4}
            y={activity.y + activity.height + 6}
            width={getPerformerWidth(performer.name) + 8}
            height={18}
            fill={theme === 'light' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'}
            rx={3}
          />
          <text
            x={activity.x + computedWidth / 2}
            y={activity.y + activity.height + 15}
            textAnchor="middle"
            dominantBaseline="central"
            fill={theme === 'light' ? '#333333' : '#ffffff'}
            fontSize={11}
            fontFamily="Arial, sans-serif"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {performer.type === 'SYSTEM' ? '\u2699 ' : '\u{1F464} '}
            {performer.name}
          </text>
        </g>
      )}
    </g>
  )
}

function getPerformerWidth(name: string): number {
  return Math.max(40, name.length * 7 + 20)
}
